import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Optional, Output, Renderer2, Self, Injector, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AppComponentBase } from '@shared/app-component-base';

export type GenericInputType = 'text' | 'number' | 'email' | 'password' | 'tel';
export type GenericInputVariant = 'input' | 'textarea';
export type GenericInputCustomErrorMessages = { [errorKey: string]: string | ((error: any) => string) };

@Component({
  selector: 'app-generic-input',
  templateUrl: './generic-input.component.html',
  styleUrls: ['./generic-input.component.scss']
})
export class GenericInputComponent extends AppComponentBase implements ControlValueAccessor, OnInit, OnDestroy, AfterViewInit {
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() type: GenericInputType = 'text';
  @Input() variant: GenericInputVariant = 'input';
  @Input() rows: number = 2;
  @Input() appearance: 'outline' | 'fill' | 'legacy' | 'standard' = 'fill';
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() maxlength?: string | number;
  @Input() mask?: string;
  @Input() specialCharacters?: string[];
  @Input() prefixIcon?: string;
  @Input() suffixIcon?: string;
  @Input() prefixText?: string;
  @Input() suffixText?: string;
  @Input() hint?: string;
  @Input() showError: boolean = true;
  @Input() customErrorMessages?: GenericInputCustomErrorMessages;

  @Output() enterPress = new EventEmitter<void>();
  @Output() suffixIconClick = new EventEmitter<void>();

  value: any;

  passwordVisible: boolean = false;

  get effectiveType(): GenericInputType {
    return this.type === 'password' && this.passwordVisible ? 'text' : this.type;
  }

  // Cuando hay [mask], el <input> interno se maneja con este FormControl propio (vía
  // [formControl] en el template) en vez del binding manual [value]/(input). ngx-mask
  // necesita un control real de Angular Forms en el MISMO elemento para poder
  // reformatear y preservar la posición del cursor correctamente — sin esto, el
  // valor se mostraba pero el cursor saltaba al principio en cada tecla/click.
  maskedControl = new FormControl();
  private maskedControlSub?: Subscription;

  @ViewChild('nativeInput') nativeInput?: ElementRef<HTMLInputElement>;
  @ViewChild('nativeTextarea') nativeTextarea?: ElementRef<HTMLTextAreaElement>;

  onChange: any = () => { };
  onTouched: any = () => { };

  constructor(
    injector: Injector,
    private renderer: Renderer2,
    @Optional() @Self() public ngControl: NgControl
  ) {
    super(injector);
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    if (this.mask) {
      this.maskedControlSub = this.maskedControl.valueChanges.subscribe((value) => {
        this.value = value;
        this.onChange(this.stripMaskCharacters(value));
      });
    }
  }

  ngAfterViewInit(): void {
    if (!this.mask) {
      this.applyValueToNativeElement();
    }
    // cdkFocusInitial en <mat-dialog>: MatDialog busca [cdkFocusInitial] en el DOM y le
    // llama .focus() directamente. Como acá ese atributo queda en el host <app-generic-input>
    // (no un <input> nativo), ese .focus() no hace nada — se reenvía manualmente al input
    // interno si el atributo está presente.
    if ((this.elementRef.nativeElement as HTMLElement).hasAttribute('cdkFocusInitial')) {
      Promise.resolve().then(() => {
        (this.nativeInput?.nativeElement ?? this.nativeTextarea?.nativeElement)?.focus();
      });
    }
  }

  ngOnDestroy(): void {
    this.maskedControlSub?.unsubscribe();
  }

  writeValue(val: any): void {
    this.value = val;
    if (this.mask) {
      this.maskedControl.setValue(val, { emitEvent: false });
    } else {
      this.applyValueToNativeElement();
    }
  }

  private applyValueToNativeElement(): void {
    // Solo aplica al campo NO enmascarado: el valor se setea de forma imperativa (no
    // vía [value] en el template) porque con binding declarativo Angular reasigna la
    // propiedad `value` del DOM en cada change detection aunque el valor no haya
    // cambiado, y el navegador resetea el cursor en cada reasignación.
    const el = this.nativeInput?.nativeElement ?? this.nativeTextarea?.nativeElement;
    if (el) {
      this.renderer.setProperty(el, 'value', this.value ?? '');
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (this.mask) {
      isDisabled ? this.maskedControl.disable({ emitEvent: false }) : this.maskedControl.enable({ emitEvent: false });
    }
  }

  onInputChange(value: any): void {
    this.value = value;
    this.onChange(value);
  }

  private stripMaskCharacters(value: any): any {
    if (!value || !this.specialCharacters || this.specialCharacters.length === 0) {
      return value;
    }
    const escaped = this.specialCharacters.map((c) => c.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    return value.toString().replace(new RegExp(`[${escaped.join('')}]`, 'g'), '');
  }

  onEnter(): void {
    this.enterPress.emit();
  }

  onSuffixIconClick(): void {
    this.suffixIconClick.emit();
  }

  togglePasswordVisibility(): void {
    this.passwordVisible = !this.passwordVisible;
  }

  getErrorMessages(): string {
    if (!this.ngControl || !this.ngControl.errors) {
      return '';
    }

    const defaultKeys = ['required', 'maxlength', 'minlength', 'pattern', 'email'];
    // Los errores custom (validadores propios) se evalúan después de los estándar.
    const customKeys = Object.keys(this.customErrorMessages ?? {}).filter((k) => !defaultKeys.includes(k));
    const errorKeys = [...defaultKeys, ...customKeys];
    for (const errorKey of errorKeys) {
      if (this.ngControl.hasError(errorKey)) {
        if (this.customErrorMessages && this.customErrorMessages[errorKey]) {
          const customMessage = this.customErrorMessages[errorKey];
          return typeof customMessage === 'function'
            ? customMessage(this.ngControl.getError(errorKey))
            : customMessage;
        }
        return this.getDefaultErrorMessage(errorKey);
      }
    }

    return '';
  }

  private getDefaultErrorMessage(errorKey: string): string {
    switch (errorKey) {
      case 'required':
        return this.l('ThisFieldIsRequired');
      case 'maxlength':
        return this.l('ThisFieldIsToLarge');
      case 'minlength':
        return this.l('ThisFieldIsToShort');
      case 'pattern':
        return this.l('InvalidPattern');
      case 'email':
        return this.l('InvalidEmailAddress');
      default:
        return '';
    }
  }
}
